import { sageBaseDeviceAuditApi as api } from './sageBaseDeviceAuditApi';

export const addTagTypes = ['auditSessions', 'deviceQaSessions', 'alertLocationSessions'] as const;
const injectedRtkApi = api
    .enhanceEndpoints({
        addTagTypes,
    })
    .injectEndpoints({
        endpoints: (build) => ({
            createAuditSession: build.mutation<
                CreateAuditSessionApiResponse,
                CreateAuditSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/auditSessions`,
                    method: 'POST',
                    body: queryArg.createAuditSessionRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions', 'deviceQaSessions'],
            }),
            getLatestCommunityAuditSessions: build.query<
                GetLatestCommunityAuditSessionsApiResponse,
                GetLatestCommunityAuditSessionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/auditSessions/facilities/${queryArg.facilityRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { sessionType: queryArg.sessionType, status: queryArg.status },
                }),
                providesTags: ['auditSessions'],
            }),
            deleteCommunityAuditSessions: build.mutation<
                DeleteCommunityAuditSessionsApiResponse,
                DeleteCommunityAuditSessionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/auditSessions/facilities/${queryArg.facilityRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { sessionType: queryArg.sessionType },
                }),
                invalidatesTags: ['auditSessions', 'deviceQaSessions', 'alertLocationSessions'],
            }),
            getRecordsForAuditSessions: build.query<
                GetRecordsForAuditSessionsApiResponse,
                GetRecordsForAuditSessionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/auditSessions/records`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                    params: { auditSessionRids: queryArg.auditSessionRids },
                }),
                providesTags: ['auditSessions', 'alertLocationSessions', 'deviceQaSessions'],
            }),
            deleteAuditSession: build.mutation<
                DeleteAuditSessionApiResponse,
                DeleteAuditSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/auditSessions/${queryArg.auditSessionRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions'],
            }),
            updateAuditSession: build.mutation<
                UpdateAuditSessionApiResponse,
                UpdateAuditSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/auditSessions/${queryArg.auditSessionRid}`,
                    method: 'PUT',
                    body: queryArg.updateAuditSessionRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions'],
            }),
            terminateAlertLocationSession: build.mutation<
                TerminateAlertLocationSessionApiResponse,
                TerminateAlertLocationSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/auditSessions/${queryArg.auditSessionRid}/terminateAlertLocations`,
                    method: 'POST',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions', 'alertLocationSessions'],
            }),
            createDeviceQaSession: build.mutation<
                CreateDeviceQaSessionApiResponse,
                CreateDeviceQaSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/deviceQaSessions`,
                    method: 'POST',
                    body: queryArg.createDeviceQaSessionRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions', 'deviceQaSessions'],
            }),
            getCommunityDeviceQaSessions: build.query<
                GetCommunityDeviceQaSessionsApiResponse,
                GetCommunityDeviceQaSessionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/deviceQaSessions/facilities/${queryArg.facilityRid}`,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                providesTags: ['deviceQaSessions'],
            }),
            updateDeviceQaSession: build.mutation<
                UpdateDeviceQaSessionApiResponse,
                UpdateDeviceQaSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/deviceQaSessions/${queryArg.deviceQaSessionRid}`,
                    method: 'PUT',
                    body: queryArg.updateDeviceQaSessionRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions', 'deviceQaSessions'],
            }),
            deleteDeviceQaSession: build.mutation<
                DeleteDeviceQaSessionApiResponse,
                DeleteDeviceQaSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/deviceQaSessions/${queryArg.deviceQaSessionRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions', 'deviceQaSessions'],
            }),
            bulkUpdateDeviceQaSessions: build.mutation<
                BulkUpdateDeviceQaSessionsApiResponse,
                BulkUpdateDeviceQaSessionsApiArg
            >({
                query: (queryArg) => ({
                    url: `/deviceQaSessions/bulk`,
                    method: 'PUT',
                    body: queryArg.body,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions', 'deviceQaSessions'],
            }),
            createAlertLocationSession: build.mutation<
                CreateAlertLocationSessionApiResponse,
                CreateAlertLocationSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/alertLocationSessions`,
                    method: 'POST',
                    body: queryArg.createAlertLocationSessionRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions', 'alertLocationSessions'],
            }),
            updateAlertLocationSession: build.mutation<
                UpdateAlertLocationSessionApiResponse,
                UpdateAlertLocationSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/alertLocationSessions/${queryArg.alertLocationSessionRid}`,
                    method: 'PUT',
                    body: queryArg.updateAlertLocationSessionRequest,
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions', 'alertLocationSessions'],
            }),
            deleteAlertLocationSession: build.mutation<
                DeleteAlertLocationSessionApiResponse,
                DeleteAlertLocationSessionApiArg
            >({
                query: (queryArg) => ({
                    url: `/alertLocationSessions/${queryArg.alertLocationSessionRid}`,
                    method: 'DELETE',
                    headers: {
                        Authorization: queryArg?.authorization,
                        'x-sage-profile-rid': queryArg?.['x-sage-profile-rid'] ?? undefined,
                    },
                }),
                invalidatesTags: ['auditSessions', 'alertLocationSessions'],
            }),
        }),
        overrideExisting: false,
    });
export { injectedRtkApi as sageDeviceAuditApi };
export type CreateAuditSessionApiResponse = /** status 200 OK */ {
    rid?: string;
};
export type CreateAuditSessionApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    createAuditSessionRequest: CreateAuditSessionRequest;
};
export type GetLatestCommunityAuditSessionsApiResponse = /** status 200 OK */ AuditSession[];
export type GetLatestCommunityAuditSessionsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    facilityRid: string;
    sessionType?: AuditSessionType[];
    status?: AuditSessionStatus[];
};
export type DeleteCommunityAuditSessionsApiResponse = /** status 200 OK */ AuditSession[];
export type DeleteCommunityAuditSessionsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    facilityRid: string;
    sessionType?: AuditSessionType[];
};
export type GetRecordsForAuditSessionsApiResponse = /** status 200 OK */ AuditSessionRecords[];
export type GetRecordsForAuditSessionsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    auditSessionRids?: string[];
};
export type DeleteAuditSessionApiResponse = /** status 200 OK */ AuditSession;
export type DeleteAuditSessionApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    auditSessionRid: string;
};
export type UpdateAuditSessionApiResponse = /** status 200 OK */ RidResponse;
export type UpdateAuditSessionApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    auditSessionRid: string;
    updateAuditSessionRequest: UpdateAuditSessionRequest;
};
export type TerminateAlertLocationSessionApiResponse = /** status 200 OK */ RidResponse[];
export type TerminateAlertLocationSessionApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    auditSessionRid: string;
};
export type CreateDeviceQaSessionApiResponse = /** status 200 OK */ RidResponse;
export type CreateDeviceQaSessionApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    createDeviceQaSessionRequest: CreateDeviceQaSessionRequest;
};
export type GetCommunityDeviceQaSessionsApiResponse = /** status 200 OK */ DeviceQaSession[];
export type GetCommunityDeviceQaSessionsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    facilityRid: string;
};
export type UpdateDeviceQaSessionApiResponse = /** status 200 OK */ RidResponse;
export type UpdateDeviceQaSessionApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    deviceQaSessionRid: string;
    updateDeviceQaSessionRequest: UpdateDeviceQaSessionRequest;
};
export type DeleteDeviceQaSessionApiResponse = /** status 200 OK */ DeviceQaSession;
export type DeleteDeviceQaSessionApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    deviceQaSessionRid: string;
};
export type BulkUpdateDeviceQaSessionsApiResponse = /** status 200 OK */ string[];
export type BulkUpdateDeviceQaSessionsApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    body: BulkUpdateDeviceQaSessionsRequest[];
};
export type CreateAlertLocationSessionApiResponse = /** status 200 OK */ RidResponse;
export type CreateAlertLocationSessionApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    createAlertLocationSessionRequest: CreateAlertLocationSessionRequest;
};
export type UpdateAlertLocationSessionApiResponse = /** status 200 OK */ RidResponse;
export type UpdateAlertLocationSessionApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    alertLocationSessionRid?: string;
    updateAlertLocationSessionRequest: UpdateAlertLocationSessionRequest;
};
export type DeleteAlertLocationSessionApiResponse = /** status 200 OK */ AlertLocationSession;
export type DeleteAlertLocationSessionApiArg = {
    authorization?: string;
    'x-sage-profile-rid'?: string | null;
    alertLocationSessionRid?: string;
};
export type AuditSessionType = 'DEVICE_QA' | 'ALERT_LOCATION_TRAINING' | 'BUTTON_INSTALLATION';
export type BulkCreateDeviceQaSessionRequest = {
    deviceAllocationRidsToAdd?: string[];
};
export type BulkCreateAlertLocationSessionRequest = {
    unitRidsToAdd?: string[];
};
export type CreateAuditSessionRequest =
    | {
          facilityRid: string;
          sessionType: AuditSessionType;
          deviceQaSessionRequest: BulkCreateDeviceQaSessionRequest;
          alertLocationSessionRequest?: BulkCreateAlertLocationSessionRequest;
      }
    | {
          facilityRid: string;
          sessionType: AuditSessionType;
          deviceQaSessionRequest?: BulkCreateDeviceQaSessionRequest;
          alertLocationSessionRequest: BulkCreateAlertLocationSessionRequest;
      };
export type AuditSessionStatus = 'IN_PROGRESS' | 'COMPLETED';
export type AuditSession = {
    rid: string;
    facilityRid: string;
    sessionType: AuditSessionType;
    status: AuditSessionStatus;
    sessionStartTimeInEpochMs?: number;
    sessionEndTimeInEpochMs?: number;
};
export type AlertLocationSessionStatus =
    | 'NEEDS_TRAINING'
    | 'IN_PROGRESS'
    | 'CANNOT_TRAIN'
    | 'FAILED'
    | 'SUCCESS';
export type AlertLocationSession = {
    rid: string;
    auditSessionRid: string;
    unitRid: string;
    status: AlertLocationSessionStatus;
    sessionStartTimeInEpochMs?: number;
    sessionEndTimeInEpochMs?: number;
    silentButtonHardwareId?: string;
    assigneeRid?: string;
};
export type DeviceQaSessionStatus = 'NEEDS_QA' | 'IN_PROGRESS' | 'FAILED' | 'SUCCESS';
export type DeviceQaSession = {
    rid: string;
    auditSessionRid: string;
    assigneeRid?: string;
    deviceAllocationRid: string;
    status: DeviceQaSessionStatus;
    sessionStartTimeInEpochMs?: number;
    sessionEndTimeInEpochMs?: number;
};
export type AuditSessionRecords = AuditSession & {
    alertLocationSessions?: AlertLocationSession[];
    deviceQaSessions?: DeviceQaSession[];
};
export type RidResponse = {
    rid?: string;
};
export type UpdateAuditSessionRequest = {
    status?: AuditSessionStatus;
    deviceQaSessionRequests?: BulkCreateDeviceQaSessionRequest;
    alertLocationSessionRequests?: BulkCreateAlertLocationSessionRequest;
};
export type CreateDeviceQaSessionRequest = {
    auditSessionRid: string;
    deviceAllocationRid: string;
    assigneeRid?: string;
};
export type UpdateDeviceQaSessionRequest = {
    assigneeRid?: string;
    status?: DeviceQaSessionStatus;
};
export type BulkUpdateDeviceQaSessionsRequest = UpdateDeviceQaSessionRequest & {
    deviceQaSessionRid?: string;
};
export type CreateAlertLocationSessionRequest = {
    auditSessionRid: string;
    silentButtonHardwareId?: string;
    unitRid: string;
    assigneeRid?: string;
};
export type UpdateAlertLocationSessionRequest = {
    silentButtonHardwareId?: string;
    status?: AlertLocationSessionStatus;
    assigneeRid?: string;
};
export const {
    useCreateAuditSessionMutation,
    useGetLatestCommunityAuditSessionsQuery,
    useLazyGetLatestCommunityAuditSessionsQuery,
    useDeleteCommunityAuditSessionsMutation,
    useGetRecordsForAuditSessionsQuery,
    useLazyGetRecordsForAuditSessionsQuery,
    useDeleteAuditSessionMutation,
    useUpdateAuditSessionMutation,
    useTerminateAlertLocationSessionMutation,
    useCreateDeviceQaSessionMutation,
    useGetCommunityDeviceQaSessionsQuery,
    useLazyGetCommunityDeviceQaSessionsQuery,
    useUpdateDeviceQaSessionMutation,
    useDeleteDeviceQaSessionMutation,
    useBulkUpdateDeviceQaSessionsMutation,
    useCreateAlertLocationSessionMutation,
    useUpdateAlertLocationSessionMutation,
    useDeleteAlertLocationSessionMutation,
} = injectedRtkApi;
